import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User as FirebaseUser,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types/user.types';
import { clearBidEligibilityCache } from '../services/bidding.service';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string, auctionHouseName: string) => Promise<void>;
  /**
   * Sends Firebase's built-in password-reset email.
   *
   * ACCOUNT ENUMERATION: this resolves IDENTICALLY whether or not the address has
   * an account. Firebase's email-enumeration protection (on by default) already
   * suppresses auth/user-not-found; we mirror that behaviour rather than working
   * around it, so the response is the same either way even if that project setting
   * is ever turned off. Every OTHER error (invalid address, rate limit, network)
   * still rejects and must be surfaced to the user.
   */
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Any sign-in / sign-out / account switch: drop every cached bid-gate pass, so a shared
      // browser never carries one person's pass into another person's session.
      clearBidEligibilityCache();
      setCurrentUser(user);

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data() as User);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email: string, password: string, auctionHouseName: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    
    const userProfile: User = {
      uid: credential.user.uid,
      email: credential.user.email!,
      role: 'auctioneer',
      auctionHouseId: auctionHouseName.toLowerCase().replace(/\s+/g, '-'),
      auctionHouseName,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(doc(db, 'users', credential.user.uid), userProfile);
  };

  const logout = async () => {
    clearBidEligibilityCache(); // before the sign-out completes, not only when the listener fires
    await firebaseSignOut(auth);
  };

  /** See the interface doc — resolves the same for known and unknown addresses. */
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
    } catch (err: any) {
      // With enumeration protection ON (the default) this branch never runs — Firebase
      // resolves successfully for an unknown address. Swallowing it keeps the outcome
      // identical if that protection is ever disabled in the console. Nothing else is
      // caught here: invalid-email / too-many-requests / network errors all propagate.
      if (err?.code !== 'auth/user-not-found') throw err;
    }
  };

  // GUEST BIDDING REMOVED (2026-10-15). There was a signInAsGuestBidder here that created a bidder
  // from an email + typed name, with a password derived from the email. Schmidt Auctions is a
  // licensed PA auction house: every bidder registers (ID seen) and has a card on file before
  // bidding. There is deliberately NO quick/guest path — if one reappears, it is a regression.

  return (
    <AuthContext.Provider value={{
      currentUser,
      userProfile,
      login,
      signup,
      resetPassword,
      logout,
      loading,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
